import { useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GameClientContext } from '../contexts/GameClientContext';
import GameClient from '../services/GameClient';
import { PageInfo, CellInfo, GameStatusInfo } from '../GameDefinitions';
import { buildTokenHistoryFromFormula, NON_NUMBER_TOKEN_INDEX } from './tokenHistory';
import type { PreferredNumberToken } from './tokenHistory';

export const useGameLogic = () => {
    console.log("useGameLogic called at", new Date().toISOString());
    const navigate = useNavigate();
    const { client, setGameClient } = useContext(GameClientContext);
    
    const initialFormula = client?.getCurrentPlayerFormula() || '';
    const initialGameNumbers = client?.getGameNumbersString() || [];
    const [playerName, setPlayerName] = useState(client?.getPlayerName() || '');
    const [formula, setFormula] = useState(initialFormula);
    const [result, setResult] = useState(client?.getCurrentPlayerResult() || '0+default');
    const [error, setError] = useState(client?.getCurrentPlayerError() || 'default error');
    const [otherPlayers, setOtherPlayers] = useState<{ [playerName: string]: { formula: string, value: string } }>({});
    const [gameNumbers, setGameNumbers] = useState<string[]>(initialGameNumbers);
    const [tokenHistory, setTokenHistory] = useState<number[]>(
        buildTokenHistoryFromFormula(initialFormula, initialGameNumbers)
    );
    const usedButtonIndices = useMemo(
        () => tokenHistory.filter((index) => index > NON_NUMBER_TOKEN_INDEX),
        [tokenHistory]
    );
    const [winner, setWinner] = useState<string>('');
    const [winnerFormula, setWinnerFormula] = useState<string>('');
    const [isTimerRunning, setIsTimerRunning] = useState(true);
    //const [gameTime, setGameTime] = useState<number>(0);
    const [isCountingDown, setIsCountingDown] = useState(false);
    //const [count, setCount] = useState(3);

    const updateDisplay = (client: GameClient) => {
        setFormula(client.getCurrentPlayerFormula());
        setError(client.getCurrentPlayerError());
        setResult(client.getCurrentPlayerResult() + client.getCurrentPlayerError());
        const newNumbers = client.getGameNumbersString();
        if (!arraysEqual(newNumbers, gameNumbers)) {
            setGameNumbers(newNumbers);
        }
        console.log('compared numbers and ' + arraysEqual(newNumbers, gameNumbers));
        setOtherPlayers(client.getOtherPlayers());
        setWinner(client.getWinner());
        setWinnerFormula(client.getWinnerFormula());
        console.log('updateDisplay');
    };

    const updateGameState = (data: PageInfo) => {
        if (!client) {
            return;
        }
        setFormula(client.getCurrentPlayerFormula());
        setError(client.getCurrentPlayerError());
        setResult(client.getCurrentPlayerResult() + client.getCurrentPlayerError());
        const newNumbers = client.getGameNumbersString();
        if (!arraysEqual(newNumbers, gameNumbers)) {
            setGameNumbers(newNumbers);
        }
        console.log('compared numbers and ' + arraysEqual(newNumbers, gameNumbers));
        setOtherPlayers(client.getOtherPlayers());
        setWinner(client.getWinner());
        setWinnerFormula(client.getWinnerFormula());
        console.log('updateGameState');
    }

    const arraysEqual = (a: string[], b: string[]) => {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
          if (a[i] !== b[i]) return false;
        }
        return true;
    }

    const updateGameNumbers = () => {
        if (!client) {
            return;
        }
        if (client.getGameNumbersString() !== gameNumbers) {
            setGameNumbers(client.getGameNumbersString());
        }
    }

    const syncTokenHistoryFromClient = (
        client: GameClient,
        preferredNumberToken?: PreferredNumberToken
    ) => {
        const nextFormula = client.getCurrentPlayerFormula();
        const nextGameNumbers = client.getGameNumbersString();

        setTokenHistory((previousHistory) => buildTokenHistoryFromFormula(
            nextFormula,
            nextGameNumbers,
            previousHistory,
            preferredNumberToken
        ));
    };

    const handleTokenClick = async (token: string) => {
        if (!client) return;

        try {
            await client.addToken(token);
            updateDisplay(client);
            syncTokenHistoryFromClient(client);
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleNumberTokenClick = async (token: string, index: number) => {
        if (!client) return;
        if (usedButtonIndices.includes(index)) return;

        try {
            await client.addToken(token);
            updateDisplay(client);
            syncTokenHistoryFromClient(client, { index, value: token });
            
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleRemove = async () => {
        if (!client) return;
        try {
            await client.removeToken();
            updateDisplay(client);
            syncTokenHistoryFromClient(client);
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleClear = async () => {
        if (!client) return;
        try {
            await client.clearFormula();
            updateDisplay(client);
            setTokenHistory([]);
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleNewGame = async () => {
        if (!client) return;
        try {
            await client.startNewGame();
            if (isTimerRunning) {
                setIsTimerRunning(false);
            }
            updateDisplay(client);
            setTokenHistory([]);

            setTimeout(() => {
                setIsTimerRunning(true); 
            }, 0);
            
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleLogout = async () => {
        if (!client) return;
        try {
            await client.exitGame();
            client.resetClient();
            setGameClient(null);
            navigate('/');
        } catch (error) {
            console.error('Error:', error);
        }
    };


    useEffect(() => {
        setTokenHistory((previousHistory) => buildTokenHistoryFromFormula(
            formula,
            gameNumbers,
            previousHistory
        ));
    }, [formula, gameNumbers]);


    useEffect(() => {
        if (winner) {
            setIsTimerRunning(false);
        }
    }, [winner]);

    /** 
    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (isCountingDown && count > 1) {
            timer = setTimeout(() => setCount(prev => prev - 1), 1000);
        } else if (count === 1) {
            timer = setTimeout(() => {
                setIsCountingDown(false);
                setCount(3); 
            }, 1000);
        }
        return () => clearTimeout(timer);
    }, [count, isCountingDown]);

    */
   /** 
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (!isCountingDown && !winner) {
            interval = setInterval(() => {
                    setGameTime(prev => prev + 0.01);
                    console.log('GameTime:', gameTime);
            },10);
        }
        return () => clearInterval(interval);
    }, [isCountingDown, winner]);
    */

    return {
        client,
        playerName,
        formula,
        result,
        error,
        otherPlayers,
        gameNumbers,
        usedButtonIndices,
        winner,
        winnerFormula,
        //gameTime,
        isCountingDown,
        isTimerRunning,
        //count,
        updateDisplay,
        updateGameState,
        handleTokenClick,
        handleNumberTokenClick,
        handleRemove,
        handleClear,
        handleNewGame,
        handleLogout
    }
}
